import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import { formatDay } from '../../format'
import { onDark } from '../../theme'
import EmptyNote from '../../components/EmptyNote'
import Section from '../../components/Section'
import CareTeamAvatar from '../chat/CareTeamAvatar'
import { useCareTeamRoster } from '../chat/useCareTeamRoster'
import ManageCareTeamDialog from './ManageCareTeamDialog'
import { useCareTeamMembers } from './useCareTeamMembers'
import { useCareTeamTemplate } from './useCareTeamTemplate'
import { roleLabelFor } from './roleCategories'
import { groupMembersByCategory, labelForMember } from './types'

type Props = { uid: string; programId: string }

const CareTeamSection = ({ uid, programId }: Props) => {
  const { template, addCategory, removeCategory } = useCareTeamTemplate(programId)
  const { members, addMember, removeMember } = useCareTeamMembers(uid)
  const { nameFor } = useCareTeamRoster(uid)
  const [manageOpen, setManageOpen] = useState(false)

  const groups = groupMembersByCategory(template.categoryIds, members)

  return (
    <Section
      title="Advanced care team"
      action={
        <Tooltip title="Edit care team members">
          <IconButton
            size="small"
            aria-label="Edit care team members"
            onClick={() => setManageOpen(true)}
            sx={{ color: onDark.secondary }}
          >
            <EditRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {groups.length === 0 ? (
        <EmptyNote>No roles set for this program yet.</EmptyNote>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groups.flatMap((group) => {
            const categoryLabel = roleLabelFor(group.categoryId)
            const rows = group.members.length > 0 ? group.members : [null]

            return rows.map((member, index) => (
              <Box
                key={member ? member.memberId : `${group.categoryId}-unassigned`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                <CareTeamAvatar
                  name={member ? nameFor(member.providerUid) : ''}
                  categoryId={member ? group.categoryId : null}
                  size={32}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: member ? onDark.primary : onDark.secondary, fontWeight: member ? 600 : 400 }}
                  >
                    {member ? <CoachName coachID={member.providerUid} /> : 'Unassigned'}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: onDark.secondary }}>
                    {labelForMember(categoryLabel, index)}
                    {member && ` · since ${formatDay(member.startedAt)}`}
                  </Typography>
                </Box>
              </Box>
            ))
          })}
        </Box>
      )}

      <ManageCareTeamDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        editMode
        template={template}
        members={members}
        onAddCategory={addCategory}
        onRemoveCategory={removeCategory}
        onAddMember={addMember}
        onRemoveMember={removeMember}
      />
    </Section>
  )
}

export default CareTeamSection
